/**
 * PRECOMMIT guarantee (W) — Graded monad structure.
 *
 * Neutralizes time reversal (G3) by requiring effects to be precommitted
 * before execution. Once an effect is declared, it cannot be retroactively
 * altered or hidden.
 *
 * Agda source: Kernel/PRECOMMIT/Neutralization.agda
 * Realization fidelity: FULL (effect types enforced at compile time)
 * Safety class: Compositional (innate immunity)
 * Neutralization mode: IMMUNE — violations have no type representation
 * Tier: 1 (type-theoretic)
 * Legacy name: IRREV
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { Brand, Guarantee } from "../foundation/types.js";
import { PRECOMMIT_META } from "../shared/theorem-meta.js";
export { PRECOMMIT_META };

// ── Structure: Graded monad ────────────────────────────────────

/** An effect grade — declares what kind of side effect an operation has. */
export type EffectGrade<G extends string> = Brand<G, "EffectGrade">;

/** A precommitted effect — the effect grade is fixed at creation. */
export interface PrecommittedEffect<G extends string, T> {
  readonly grade: EffectGrade<G>;
  readonly execute: () => T;
  readonly _committed: true;
}

/** Create a precommitted effect. Once created, the grade cannot change. */
export function precommit<G extends string, T>(
  grade: G,
  execute: () => T,
): PrecommittedEffect<G, T> {
  return {
    grade: grade as EffectGrade<G>,
    execute,
    _committed: true,
  };
}

/** Sequence two precommitted effects (graded monad bind). */
export function sequence<FirstGrade extends string, SecondGrade extends string, A, B>(
  first: PrecommittedEffect<FirstGrade, A>,
  secondGrade: SecondGrade,
  next: (a: A) => PrecommittedEffect<SecondGrade, B>,
): PrecommittedEffect<`${FirstGrade}+${SecondGrade}`, B> {
  // The combined grade is known immediately from first.grade and secondGrade —
  // no lazy evaluation or mutable state required. The grade is honest at all
  // times. TypeScript preserves the template-literal grade from the already
  // fixed FirstGrade and SecondGrade values.
  return precommit(`${first.grade}+${secondGrade}`, (): B => {
    const a = first.execute();
    return next(a).execute();
  });
}

export interface PrecommitViolation {
  readonly __brand: "PrecommitViolation";
  readonly kind: "retroactive-alteration";
}

export interface PrecommitStructure<G extends string> {
  readonly allowedGrades: readonly G[];
  readonly gradeOrder: (g1: G, g2: G) => boolean;
}

export function createPrecommitGuarantee<G extends string>(
  structure: PrecommitStructure<G>,
): Guarantee<PrecommitStructure<G>, PrecommitViolation> {
  return {
    structure,
    neutralize: (_violation: PrecommitViolation): never => {
      throw new Error(
        "PRECOMMIT violation is structurally inexpressible: " +
          "effect grades are fixed at creation and cannot be altered",
      );
    },
    meta: PRECOMMIT_META,
  };
}
